/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { BEM } from "@adapptio/lib-components";
import React, { FC, useContext, useEffect, useRef } from "react";
import { FieldSectionContext } from "./FieldSection";

export interface IFieldSectionContentProps {
	className?: string;
	padded?: boolean;
}

export const FieldSectionContent: FC<IFieldSectionContentProps> = ({ children, className, padded }) => {
	const sectionCtx = useContext(FieldSectionContext);
	const lastCollapsed = useRef(sectionCtx?.isCollapsed || false);
	const rootElRef = useRef<HTMLDivElement>();

	useEffect(() => {
		if (sectionCtx?.isCollapsed && rootElRef) {
			rootElRef.current.style.height = "0px";
		}
	}, []);

	useEffect(() => {
		if (!rootElRef.current) {
			return;
		}

		const isCollapsed = sectionCtx?.isCollapsed;
		let t;

		// Collapse
		if (isCollapsed && !lastCollapsed.current) {
			rootElRef.current.style.height = rootElRef.current.scrollHeight + "px";

			t = setTimeout(() => {
				rootElRef.current.style.height = "0px";
			}, 100); // 50
			// Expand
		} else if (!isCollapsed && lastCollapsed.current) {
			rootElRef.current.style.height = rootElRef.current.scrollHeight + "px";

			t = setTimeout(() => {
				rootElRef.current.style.height = "auto";
			}, 500); // 400
		}

		lastCollapsed.current = isCollapsed;

		return () => {
			if (t) {
				clearTimeout(t);
			}
		};
	}, [ sectionCtx?.isCollapsed, rootElRef.current ]);

	const baseClass = BEM("edt-field-section", "content", {
		padded: padded
	});

	if (className) {
		baseClass.add(className);
	}

	return (
		<div className={baseClass.toClassName()} ref={rootElRef}>
			<div className={baseClass.element("content-inner").toClassName()}>
				<FieldSectionContext.Provider value={null}>{children}</FieldSectionContext.Provider>
			</div>
		</div>
	);
};
